// src/utils/fileValidation.js
// Single source of truth for image-upload policy. Amazon accepts JPEG/PNG for
// listing images and the single_gen backend whitelists the same set, so keeping
// this in one place stops the three uploaders from drifting apart.

export const IMAGE_MAX_SIZE = 5 * 1024 * 1024; // 5MB
export const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png'];
export const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png'];

// Value for an <input type="file"> accept attribute. This is only a dialog
// filter — never treat it as validation; the OS picker can bypass it.
export const IMAGE_ACCEPT = '.jpg,.jpeg,.png,image/jpeg,image/png';

// Leading bytes every JPEG / PNG must start with, paired with the mime type they
// prove.
const SIGNATURES = [
    { bytes: [0xff, 0xd8, 0xff], mimeType: 'image/jpeg' },
    { bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a], mimeType: 'image/png' },
];

const formatSize = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)}MB`;

// Both file.type and the extension are supplied by the OS, so a renamed file
// passes either check. Reading the header is what actually pins the format.
// Returns the mime type the bytes prove, or null when they match neither.
const detectImageType = async (blob) => {
    try {
        const header = new Uint8Array(await blob.slice(0, 8).arrayBuffer());
        return SIGNATURES.find(({ bytes }) => bytes.every((byte, i) => header[i] === byte))?.mimeType ?? null;
    } catch {
        return null;
    }
};

/**
 * Validates picked images against the shared policy.
 *
 * @param {File[]} files - files from the input's change event
 * @param {{ existingCount?: number, maxCount?: number }} [options] - remaining-slot accounting
 * @returns {Promise<{ accepted: File[], errors: string[] }>} accepted files plus a
 *          per-file reason for every rejection, so callers can tell the user why.
 */
export const validateImageFiles = async (files, { existingCount = 0, maxCount = Infinity } = {}) => {
    const accepted = [];
    const errors = [];
    let remainingSlots = Math.max(maxCount - existingCount, 0);

    for (const file of files) {
        if (remainingSlots <= 0) {
            errors.push(`${file.name}: image limit of ${maxCount} reached.`);
            continue;
        }

        const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
        // file.type is empty for some sources — the signature check below is the
        // real gate, so an absent type isn't treated as a failure on its own.
        if (!IMAGE_EXTENSIONS.includes(extension) || (file.type && !IMAGE_MIME_TYPES.includes(file.type))) {
            errors.push(`${file.name}: only JPG and PNG images are allowed.`);
            continue;
        }

        if (file.size > IMAGE_MAX_SIZE) {
            errors.push(`${file.name}: ${formatSize(file.size)} exceeds the 5MB limit.`);
            continue;
        }

        if (!await detectImageType(file)) {
            errors.push(`${file.name}: contents are not a valid JPG or PNG image.`);
            continue;
        }

        accepted.push(file);
        remainingSlots -= 1;
    }

    return { accepted, errors };
};

// Longest edge kept when a processed blob has to be shrunk to fit the policy.
// The backend composites every input onto a 1000px canvas, so pixels above this
// are discarded server-side anyway.
const DOWNSCALE_MAX_EDGE = 2000;

// Re-encode at progressively smaller edge lengths until the result fits the size
// limit. The source mime type is kept so transparency survives — re-encoding a
// background-removed PNG as JPEG would flatten the alpha onto black.
const downscaleToLimit = async (blob, mimeType) => {
    const bitmap = await createImageBitmap(blob);
    try {
        const longestEdge = Math.max(bitmap.width, bitmap.height);
        // An image already under the pixel bound is over the limit on bytes
        // alone, so re-encoding it at full size would just reproduce it — start
        // one halving in.
        let maxEdge = longestEdge > DOWNSCALE_MAX_EDGE ? DOWNSCALE_MAX_EDGE : Math.round(longestEdge / 2);
        // One canvas, resized per attempt: assigning width/height clears it, and
        // a fresh canvas each round leaves several multi-MB buffers for the GC.
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        let encoded = null;
        // Bounded at four attempts, the last at an eighth of the starting edge
        // (250px or less) — far below anything that could still be oversized.
        for (let attempt = 0; attempt < 4; attempt += 1) {
            const scale = maxEdge / longestEdge;
            canvas.width = Math.max(1, Math.round(bitmap.width * scale));
            canvas.height = Math.max(1, Math.round(bitmap.height * scale));
            context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
            encoded = await new Promise(resolve => canvas.toBlob(resolve, mimeType));
            if (encoded && encoded.size <= IMAGE_MAX_SIZE) return encoded;
            maxEdge = Math.round(maxEdge / 2);
        }
        return encoded;
    } finally {
        bitmap.close();
    }
};

/**
 * Wraps a blob returned by an image-processing service (background removal) as a
 * File that still satisfies the upload policy, because it replaces the user's
 * picked file and is submitted in its place. Two things break otherwise: the
 * service returns a lossless transparent PNG that is routinely larger than the
 * source JPEG and past the 5MB cap the backend enforces again at generate time,
 * and a failed download yields an error body that is not an image at all.
 *
 * The original filename is preserved — drafts key their stored images by it.
 *
 * @param {Blob} blob - the processed image
 * @param {string} originalName - filename of the file being replaced
 * @returns {Promise<File>} a policy-compliant File
 * @throws {Error} if the blob is not a usable JPG/PNG or cannot be brought
 *         under the size limit
 */
export const toProcessedImageFile = async (blob, originalName) => {
    // Derived from the bytes, not blob.type: a service response can carry a
    // missing or wrong Content-Type, and rejecting on that would kill a
    // perfectly good image.
    const mimeType = await detectImageType(blob);
    if (!mimeType) {
        throw new Error('Processed image is not a valid JPG or PNG.');
    }

    if (blob.size <= IMAGE_MAX_SIZE) {
        return new File([blob], originalName, { type: mimeType });
    }

    const resized = await downscaleToLimit(blob, mimeType);
    if (!resized || resized.size > IMAGE_MAX_SIZE) {
        throw new Error(`Processed image is ${formatSize(blob.size)} and cannot be reduced below the ${formatSize(IMAGE_MAX_SIZE)} limit.`);
    }
    return new File([resized], originalName, { type: mimeType });
};
